// eslint-disable-next-line simple-import-sort/imports
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import Modal from '@src/components/common/Modal';
import StatusMessageModal from '@src/components/common/StatusMessageModal';
import {
  useDatoClient,
  useUserIdentity,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';

import AddStoriesModalBanner from './AddStoriesModalBanner';
import { StepFour, StepOne, StepThree, StepTwo } from './steps';
import { Step, useStoryCreator } from './useStoryCreator';
import useUploadPhase from './useUploadPhase';
import { useMonitorStoryUpload } from '@src/monitoring';

import type { Story } from '@src/types.ts/story';

const AddStoriesModal = observer(
  ({
    closeModal,
    onStoryBlankCreated,
    onStoryInitialized,
    onStoryCreated,
    onStoryProgress,
    modalId,
    setBannerHeight,
  }: {
    closeModal: (modalId: string) => void;
    onStoryBlankCreated: (storyId: string, modalId: string) => void;
    onStoryInitialized: (s: Story, modalId: string) => void;
    onStoryCreated: (s: Story, modalId: string) => void;
    onStoryProgress: (percent: number, storyId?: string) => void;
    modalId: string;
    setBannerHeight: (height: number | undefined) => void;
  }) => {
    const videoCreator = useVideoCreatorStore();
    const userStoryUploadFlowStore = useUserStoryUploadFlow();
    const userIdentity = useUserIdentity();
    const datoClientStore = useDatoClient();
    const [isStatusMessageModalOpen, setIsStatusMessageModalOpen] =
      useState<boolean>(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    // local percent replaced by phase-aware hook; keep setter path for error handler compatibility
    const [, setLegacyPercent] = useState<number>(0);
    const handleStoryProgress = useCallback(
      (p: number, storyId?: string) => {
        setLegacyPercent(p); // backward compatibility (removed UI usage)
        userStoryUploadFlowStore.updateUploadingProgress(storyId, p);
        onStoryProgress(p, storyId);
      },
      [onStoryProgress, userStoryUploadFlowStore],
    );

    // Monitoring hook (must be early)
    const storyMonitoring = useMonitorStoryUpload();

    const { organization } = videoCreator;

    if (!organization) {
      console.error(
        'AddStoriesModal: No organization found in video creator store',
      );
      return null;
    }

    const storyCreatorParams = useMemo(
      () => ({
        videoCreator,
        organization,
        datoClientStore,
        userIdentity,
        onArtifactUploadError: (kind: 'doc' | 'video', error: unknown) => {
          storyMonitoring.onArtifactUploadFail(kind, error, {
            uploadStrategyThread: uploadMechanism,
          });
        },
        onStoryBlankCreated: (storyId: string) =>
          onStoryBlankCreated(storyId, modalId),
        onStoryInitialized: (s: Story) => onStoryInitialized(s, modalId),
        onStoryCreated: (s: Story) => onStoryCreated(s, modalId),
        onStoryProgress: handleStoryProgress,
        onError: (...data: unknown[]) => {
          const error =
            data[0] instanceof Error ? data[0] : new Error(String(data[0]));
          storyMonitoring.onVideoUploadError(error, {
            uploadStrategyThread: uploadMechanism,
          });
          storyMonitoring.onProcessingFail(error);
          storyMonitoring.onWorkflowCancel('error');
          setIsStatusMessageModalOpen(true);
          handleStoryProgress(100);
        },
      }),
      [
        videoCreator,
        datoClientStore,
        userIdentity,
        onStoryBlankCreated,
        onStoryInitialized,
        onStoryCreated,
        modalId,
        handleStoryProgress,
        storyMonitoring,
      ],
    );

    const {
      step,
      goToNextStep,
      canGoToStep,
      handleStoryVideoUpload,
      handleStoryUrlUpload,
      handleStoryBRollUpload,
      uploadMechanism,
      storyName,
      setStoryName,
      storyLanguage,
      setStoryLanguage,
      storyType,
      setStoryType,
      videoMusicStrategy,
      setVideoMusicStrategy,
      storyArtifactsFiles,
      storyArtifactsVideoFiles,
      storyArtifacts,
      storyArtifactsVideo,
      saveArtifactNotes,
      setArtifactNotes,
      deleteArtifact,
      saveArtifactVideoNotes,
      setArtifactVideoNotes,
      deleteArtifactVideo,
      maxUploadSizeGB,
      maxStoryTitleLength,
    } = useStoryCreator(storyCreatorParams);

    // Upload phase hook (after monitoring declared)
    const { phase, percent: phasePercent } = useUploadPhase();
    const prevStepRef = useRef(step);
    const prevStoryTypeRef = useRef(storyType);
    const prevStoryNameRef = useRef(storyName);
    const prevArtifactsCountRef = useRef<number>(0);
    const prevArtifactsVideoCountRef = useRef<number>(0);

    // Monitor step transitions for instantaneous actions (type selection, metadata submit)
    useEffect(() => {
      // Type selection completed when moving from step two to three
      if (
        prevStepRef.current === Step.two &&
        step === Step.three &&
        storyType
      ) {
        storyMonitoring.onStoryTypeSelected({
          storyType,
          videoMusicStrategy,
          orgId: organization.id,
        });
      }
      // Metadata submit when moving from step three to four
      if (prevStepRef.current === Step.three && step === Step.four) {
        storyMonitoring.onMetadataSubmitted({
          titleLength: storyName.length,
          hasCustomTitle: !!storyName,
          language: storyLanguage,
          allowLanguageSelect: organization.allowLangSelectAtUserStoryUpload,
          orgId: organization.id,
        });
      }
      // Processing completion when reaching terminal step
      if (step === Step.five && prevStepRef.current !== Step.five) {
        storyMonitoring.onProcessingComplete();
        storyMonitoring.onWorkflowFinalizeSuccess();
      }
      prevStepRef.current = step;
      prevStoryTypeRef.current = storyType;
      prevStoryNameRef.current = storyName;
    }, [
      step,
      storyType,
      storyName,
      storyLanguage,
      videoMusicStrategy,
      storyMonitoring,
      videoCreator,
    ]);

    // Phase transitions: when phase becomes processing we consider upload complete and start processing action
    useEffect(() => {
      if (phase.phase === 'processing') {
        storyMonitoring.onVideoUploadComplete({
          uploadStrategyThread: uploadMechanism,
        });
        storyMonitoring.onProcessingStart({
          orgId: organization.id,
        });
      }
    }, [phase.phase, storyMonitoring, videoCreator, uploadMechanism]);

    // Cancel workflow on unmount if not finished
    useEffect(() => {
      return () => {
        if (step !== Step.five) {
          storyMonitoring.onWorkflowCancel('unmount');
        }
      };
    }, [step, storyMonitoring]);

    // Instrument artifact additions (doc + video) by detecting count increases
    useEffect(() => {
      if (storyArtifactsFiles.length > prevArtifactsCountRef.current) {
        const added =
          storyArtifactsFiles.length - prevArtifactsCountRef.current;
        const newFiles = storyArtifactsFiles.slice(-added);
        storyMonitoring.onArtifactUploadStart({
          artifactKind: 'doc',
          count: added,
          totalBytes: newFiles.reduce((a, f) => a + f.size, 0),
          cumulativeCount: storyArtifactsFiles.length,
          orgId: organization.id,
        });
        storyMonitoring.onArtifactUploadComplete('doc', {
          uploadStrategyThread: uploadMechanism,
        });
      }
      prevArtifactsCountRef.current = storyArtifactsFiles.length;
    }, [storyArtifactsFiles, storyMonitoring, videoCreator, uploadMechanism]);

    useEffect(() => {
      if (
        storyArtifactsVideoFiles.length > prevArtifactsVideoCountRef.current
      ) {
        const added =
          storyArtifactsVideoFiles.length - prevArtifactsVideoCountRef.current;
        const newFiles = storyArtifactsVideoFiles.slice(-added);
        storyMonitoring.onArtifactUploadStart({
          artifactKind: 'video',
          count: added,
          totalBytes: newFiles.reduce((a, f) => a + f.size, 0),
          cumulativeCount: storyArtifactsVideoFiles.length,
          orgId: organization.id,
        });
        storyMonitoring.onArtifactUploadComplete('video', {
          uploadStrategyThread: uploadMechanism,
        });
      }
      prevArtifactsVideoCountRef.current = storyArtifactsVideoFiles.length;
    }, [
      storyArtifactsVideoFiles,
      storyMonitoring,
      videoCreator,
      uploadMechanism,
    ]);

    const renderStep = () => {
      switch (step) {
        case Step.one:
          return (
            <StepOne
              goToNextStep={goToNextStep}
              canGoToStep={canGoToStep(Step.two)}
              handleStoryVideoUpload={(files: File[]) => {
                if (files.length === 0) {
                  return;
                }
                if (files[0].size > maxUploadSizeGB * 1000 * 1000 * 1000) {
                  console.warn(
                    `File size exceeds ${maxUploadSizeGB} GB, size:`,
                    files[0].size,
                  );
                  setStatusMessage(
                    `File size exceeds the maximum upload size of ${maxUploadSizeGB} GB`,
                  );
                  setIsStatusMessageModalOpen(true);
                  return;
                }
                storyMonitoring.onSourceChosen({
                  sourceType: files.length > 1 ? 'multi_file' : 'file',
                  fileCount: files.length,
                  totalBytes: files.reduce((a, f) => a + f.size, 0),
                  isUrl: false,
                  orgId: organization.id,
                });
                storyMonitoring.onVideoUploadStart({
                  fileCount: files.length,
                  totalBytes: files.reduce((a, f) => a + f.size, 0),
                  isMultiFile: files.length > 1,
                  isUrl: false,
                  uploadStrategy: 'unknown',
                  orgId: organization.id,
                });
                handleStoryVideoUpload(files);
              }}
              handleStoryUrlUpload={handleStoryUrlUpload}
              maxUploadSizeGB={maxUploadSizeGB}
            />
          );
        case Step.two:
          return (
            <StepTwo
              goToNextStep={goToNextStep}
              canGoToStep={canGoToStep(Step.three)}
              storyType={storyType}
              setStoryType={setStoryType}
              videoMusicStrategy={videoMusicStrategy}
              setVideoMusicStrategy={setVideoMusicStrategy}
            />
          );
        case Step.three:
          return (
            <StepThree
              goToNextStep={goToNextStep}
              canGoToStep={canGoToStep(Step.four)}
              storyName={storyName}
              setStoryName={setStoryName}
              storyLanguage={storyLanguage}
              setStoryLanguage={setStoryLanguage}
              allowLangSelect={organization.allowLangSelectAtUserStoryUpload}
              maxStoryTitleLength={maxStoryTitleLength}
            />
          );

        case Step.four:
          return (
            <StepFour
              goToNextStep={goToNextStep}
              handleStoryBRollUpload={handleStoryBRollUpload}
              storyArtifactsFiles={storyArtifactsFiles}
              storyArtifactsVideoFiles={storyArtifactsVideoFiles}
              storyArtifacts={storyArtifacts}
              storyArtifactsVideo={storyArtifactsVideo}
              saveArtifactNotes={saveArtifactNotes}
              setArtifactNotes={setArtifactNotes}
              deleteArtifact={deleteArtifact}
              saveArtifactVideoNotes={saveArtifactVideoNotes}
              setArtifactVideoNotes={setArtifactVideoNotes}
              deleteArtifactVideo={deleteArtifactVideo}
            />
          );
        default:
          return null;
      }
    };

    const handleCloseModal = () => {
      closeModal(modalId);
    };

    // Hook must be declared before any conditional early returns to avoid hook order changes
    const [showUploadCompleteMsg, setShowUploadCompleteMsg] = useState(false);
    if (phase.phase === 'processing' && !showUploadCompleteMsg) {
      // fire once at transition
      setShowUploadCompleteMsg(true);
    }
    if (isStatusMessageModalOpen) {
      return (
        <StatusMessageModal
          enabled
          variant="user-upload-failed"
          customMessage={statusMessage}
          onClose={handleCloseModal}
        />
      );
    }

    if (step === Step.five) {
      return null;
    }
    const uploadPct = phasePercent ?? 0;
    const showBanner =
      (phase.phase === 'uploading' && uploadPct > 0 && uploadPct < 100) ||
      phase.phase === 'processing';

    return (
      <>
        <Modal isOpen closeModal={handleCloseModal}>
          <Wrapper>{renderStep()}</Wrapper>
        </Modal>
        {showBanner && (
          <AddStoriesModalBanner
            setBannerHeight={setBannerHeight}
            percent={phase.phase === 'uploading' ? uploadPct : 100}
            phaseLabel={
              phase.phase === 'processing' ? 'processing' : 'uploading'
            }
            showUploadCompleteMsg={showUploadCompleteMsg}
          />
        )}
      </>
    );
  },
);

const Wrapper = styled.div`
  width: 440px;
  border-radius: 16px;
  border: 1px solid #484848;
  padding: 48px 24px;
  box-sizing: content-box;
  background: #03041a;
`;

export default AddStoriesModal;
